import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SubscriptionDetailsModal from './components/SubscriptionDetailsModal';
import {
  adminSubscriptionService,
  type AdminSubscription,
  type SubscriptionInterval,
  type SubscriptionStatus,
} from '../../services/adminSubscriptionService';
import { adminPaymentLinkService } from '../../services/adminPaymentLinkService';
import { clientService, type Client } from '../../services/clientService';
import { currencyService } from '../../services/currencyService';
import { getAllServices, type Service } from '../../services/servicesStore';

const STATUS_FILTERS: Array<{ value: 'all' | SubscriptionStatus; label: string }> = [
  { value: 'all', label: 'Tous statuts' },
  { value: 'trial', label: 'Essai' },
  { value: 'active', label: 'Actif' },
  { value: 'paused', label: 'En pause' },
  { value: 'past_due', label: 'En retard' },
  { value: 'canceled', label: 'Annule' },
  { value: 'expired', label: 'Expire' },
];

const INTERVAL_FILTERS: Array<{ value: 'all' | SubscriptionInterval; label: string }> = [
  { value: 'all', label: 'Toutes frequences' },
  { value: 'monthly', label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'yearly', label: 'Annuel' },
];

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trial: 'Essai',
  active: 'Actif',
  paused: 'En pause',
  past_due: 'En retard',
  canceled: 'Annule',
  expired: 'Expire',
};

const STATUS_BADGES: Record<SubscriptionStatus, string> = {
  trial: 'bg-slate-100 text-slate-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-amber-100 text-amber-700',
  past_due: 'bg-red-100 text-red-700',
  canceled: 'bg-gray-100 text-gray-600',
  expired: 'bg-orange-100 text-orange-700',
};

const INTERVAL_LABELS: Record<SubscriptionInterval, string> = {
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel',
};

const formatDate = (value?: string) => {
  if (!value) {
    return 'Non renseigne';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return 'Non renseigne';
  }

  return parsed.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const SubscriptionManager = () => {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SubscriptionStatus>('all');
  const [intervalFilter, setIntervalFilter] = useState<'all' | SubscriptionInterval>('all');
  const [selectedSubscription, setSelectedSubscription] =
    useState<AdminSubscription | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'create'>('view');

  const loadData = async () => {
    setIsLoading(true);

    try {
      const [subscriptionsData, clientsData, servicesData] = await Promise.all([
        adminSubscriptionService.getSubscriptions(true),
        clientService.getClients(true),
        getAllServices(true),
      ]);

      setSubscriptions(subscriptionsData);
      setClients(clientsData);
      setServices(servicesData);
    } catch (error) {
      console.error(error);
      toast.error('Erreur de chargement des abonnements');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);
  const normalizedQuery = query.trim().toLowerCase();

  const filteredSubscriptions = subscriptions.filter((subscription) => {
    const matchesStatus =
      statusFilter === 'all' ? true : subscription.status === statusFilter;
    const matchesInterval =
      intervalFilter === 'all' ? true : subscription.interval === intervalFilter;
    const matchesQuery =
      !normalizedQuery ||
      [
        subscription.subscriptionNumber,
        subscription.clientName,
        subscription.clientEmail,
        subscription.clientCompany,
        subscription.serviceName,
        subscription.linkedOrderNumber,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedQuery));

    return matchesStatus && matchesInterval && matchesQuery;
  });

  const metrics = subscriptions.reduce(
    (accumulator, subscription) => {
      accumulator.total += 1;
      accumulator.revenue += currencyService.convertToDisplayAmount(
        subscription.amount,
        subscription.currencyCode,
        displayCountry
      );

      if (subscription.status === 'active') {
        accumulator.active += 1;
      }

      if (subscription.status === 'past_due') {
        accumulator.pastDue += 1;
      }

      if (subscription.status === 'paused') {
        accumulator.paused += 1;
      }

      return accumulator;
    },
    {
      total: 0,
      active: 0,
      pastDue: 0,
      paused: 0,
      revenue: 0,
    }
  );

  const handleOpenCreate = () => {
    setSelectedSubscription(adminSubscriptionService.createEmptySubscription());
    setModalMode('create');
  };

  const handleOpenView = (subscription: AdminSubscription) => {
    setSelectedSubscription(subscription);
    setModalMode('view');
  };

  const handleSave = async (subscription: AdminSubscription) => {
    try {
      const isCreateMode = modalMode === 'create';
      let subscriptionToSave = subscription;
      let createdPaymentLink = false;
      let paymentEmailSent = false;
      let paymentEmailError = '';

      if (
        isCreateMode &&
        !subscription.paymentLinkKey.trim() &&
        subscription.clientName.trim() &&
        subscription.clientEmail.trim() &&
        subscription.serviceName.trim() &&
        Number.isFinite(subscription.amount) &&
        subscription.amount > 0
      ) {
        const paymentResult = await adminPaymentLinkService.createPaymentLink({
          clientId: subscription.clientId || undefined,
          clientName: subscription.clientName,
          clientEmail: subscription.clientEmail,
          clientPhone: subscription.clientPhone || undefined,
          clientCompany: subscription.clientCompany || undefined,
          serviceId: subscription.serviceId || undefined,
          serviceName: subscription.serviceName,
          paymentMode: subscription.paymentMode || 'recurring',
          linkedSubscriptionId: subscription.id || undefined,
          linkedSubscriptionNumber: subscription.subscriptionNumber || undefined,
          recurringInterval: subscription.interval,
          recurringStartDate: subscription.nextBillingDate || undefined,
          recurringAmount: Number(subscription.amount || 0),
          amount: Number(subscription.amount || 0),
          currencyCode: subscription.currencyCode || displayCurrencyCode,
          notes: subscription.internalNotes || undefined,
          clientMessage: subscription.notes || undefined,
          sendEmail: true,
          emailTemplateAlias: 'checkout-quote',
        });

        subscriptionToSave = {
          ...subscription,
          paymentLinkKey: paymentResult.paymentLink.paymentLinkKey,
          paymentMode:
            paymentResult.paymentLink.paymentMode || subscription.paymentMode || 'recurring',
          mollieCustomerId: paymentResult.paymentLink.mollieCustomerId || '',
          mollieSubscriptionId: paymentResult.paymentLink.mollieSubscriptionId || '',
          recurringInterval:
            paymentResult.paymentLink.recurringInterval || subscription.interval,
          recurringStatus: paymentResult.paymentLink.recurringStatus || '',
          recurringActivatedAt: paymentResult.paymentLink.recurringActivatedAt || '',
        };
        createdPaymentLink = true;
        paymentEmailSent = paymentResult.emailSent;
        paymentEmailError = paymentResult.emailError;
      }

      await adminSubscriptionService.saveSubscription(subscriptionToSave);
      await loadData();
      setSelectedSubscription(null);

      if (!isCreateMode) {
        toast.success('Abonnement mis a jour !');
        return;
      }

      if (createdPaymentLink && paymentEmailSent) {
        toast.success('Abonnement cree + lien de paiement envoye par email.');
        return;
      }

      if (createdPaymentLink && paymentEmailError) {
        toast.success('Abonnement cree + lien de paiement genere.');
        toast.error(`Email non envoye: ${paymentEmailError}`);
        return;
      }

      if (createdPaymentLink) {
        toast.success('Abonnement cree + lien de paiement genere.');
        return;
      }

      toast.success('Abonnement cree avec succes !');
      toast.error(
        'Lien de paiement non genere automatiquement (verifie service, email client et montant).'
      );
    } catch (error) {
      console.error(error);
      toast.error('Impossible de sauvegarder cet abonnement');
    }
  };

  const handleDelete = async (subscription: AdminSubscription) => {
    if (
      !window.confirm(
        `Supprimer l abonnement ${subscription.subscriptionNumber} ?`
      )
    ) {
      return;
    }

    try {
      await adminSubscriptionService.removeSubscription(subscription.id);
      await loadData();
      setSelectedSubscription(null);
      toast.success('Abonnement supprime !');
    } catch (error) {
      console.error(error);
      toast.error('Impossible de supprimer cet abonnement');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Gestion des Abonnements
          </h2>
          <p className="mt-2 text-gray-600">
            Pilote les forfaits recurrents, les renouvellements, les retards et
            l avancement de delivery de chaque abonnement.
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Affichage admin actuel: {displayCurrencyCode}. Les montants d origine restent
            conserves par abonnement.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-neon-orange px-5 py-3 text-white transition-colors hover:bg-[#FF4500]"
        >
          <span className="text-lg leading-none">+</span>
          <span>Nouvel abonnement</span>
        </button>
      </div>

      <div className="mb-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Abonnements</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{metrics.total}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">Actifs</div>
          <div className="mt-2 text-3xl font-bold text-emerald-600">{metrics.active}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">En retard</div>
          <div className="mt-2 text-3xl font-bold text-red-600">{metrics.pastDue}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">En pause</div>
          <div className="mt-2 text-3xl font-bold text-amber-600">{metrics.paused}</div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="text-sm text-gray-500">MRR suivi ({displayCurrencyCode})</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {currencyService.formatCurrencyAmount(metrics.revenue, displayCurrencyCode, {
              country: displayCountry,
              display: 'code',
            })}
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_240px_240px]">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Recherche
            </label>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Abonnement, client, email, service, commande..."
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as 'all' | SubscriptionStatus)
              }
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              {STATUS_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Frequence
            </label>
            <select
              value={intervalFilter}
              onChange={(event) =>
                setIntervalFilter(event.target.value as 'all' | SubscriptionInterval)
              }
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20"
            >
              {INTERVAL_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-gray-600">
          Chargement des abonnements...
        </div>
      ) : filteredSubscriptions.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <h3 className="text-2xl font-semibold text-gray-900">
            Aucun abonnement pour le moment
          </h3>
          <p className="mx-auto mt-3 max-w-2xl text-gray-600">
            Cree le premier forfait recurrent pour suivre le statut, la facturation et
            le delivery de maniere propre dans l admin.
          </p>
          <button
            onClick={handleOpenCreate}
            className="mt-8 rounded-lg bg-neon-orange px-5 py-3 font-medium text-white transition-colors hover:bg-[#FF4500]"
          >
            Creer un abonnement
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSubscriptions.map((subscription) => {
            const displayAmount = currencyService.formatAmountForDisplay(
              subscription.amount,
              subscription.currencyCode,
              {
                country: displayCountry,
                display: 'code',
              }
            );
            const originalAmount = currencyService.formatCurrencyAmount(
              subscription.amount,
              subscription.currencyCode,
              { display: 'code' }
            );

            return (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="text-xl font-semibold text-gray-900">
                        {subscription.subscriptionNumber}
                      </h3>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          STATUS_BADGES[subscription.status]
                        }`}
                      >
                        {STATUS_LABELS[subscription.status]}
                      </span>
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                        {INTERVAL_LABELS[subscription.interval]}
                      </span>
                    </div>

                    <div className="grid gap-3 text-sm text-gray-600 md:grid-cols-2 xl:grid-cols-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Client
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {subscription.clientName}
                        </div>
                        <div>{subscription.clientEmail}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Service
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          {subscription.serviceName || 'Non renseigne'}
                        </div>
                        <div>{subscription.clientCompany || 'Sans entreprise'}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Facturation
                        </div>
                        <div className="mt-1 font-medium text-gray-900">
                          Prochaine: {formatDate(subscription.nextBillingDate)}
                        </div>
                        <div>Derniere: {formatDate(subscription.lastBillingDate)}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-gray-400">
                          Montant
                        </div>
                        <div className="mt-1 text-xl font-semibold text-neon-orange">
                          {displayAmount}
                        </div>
                        <div className="text-xs text-gray-500">Base: {originalAmount}</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => handleOpenView(subscription)}
                      className="rounded-lg bg-gray-100 px-4 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      Ouvrir
                    </button>
                    <button
                      onClick={() => handleDelete(subscription)}
                      className="rounded-lg bg-red-50 px-4 py-2 font-medium text-red-600 transition-colors hover:bg-red-100"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {selectedSubscription && (
        <SubscriptionDetailsModal
          subscription={selectedSubscription}
          clients={clients}
          services={services}
          defaultMode={modalMode}
          onClose={() => setSelectedSubscription(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default SubscriptionManager;
